import { useEffect, useRef } from 'react'
import { useReactFlow } from '@xyflow/react'
import type { RefObject } from 'react'

/**
 * Wheel and pinch zooming for the chart canvas.
 *
 * React Flow's own wheel zoom applies each notch as a jump, which on a chart
 * spread out by hand reads as the canvas snapping between sizes. This replaces
 * it with a target the wheel pushes and a camera that eases towards it, so a
 * long scroll is one continuous movement rather than a staircase.
 *
 * It drives the SAME camera the layout effect fits, through `setViewport`, so
 * the two never hold different ideas of where the chart is.
 */

/** Zoom the canvas will not go below by hand, low enough to see a big chart. */
export const CHART_MIN_ZOOM = 0.2

/** Zoom the canvas will not go above by hand. */
export const CHART_MAX_ZOOM = 1.5

/** Wheel deltas arrive in lines or pages on some browsers; normalize to px. */
const WHEEL_LINE_HEIGHT = 16
const WHEEL_PAGE_HEIGHT = 400

/** How much of a zoom a pixel of wheel travel is worth. */
const WHEEL_SENSITIVITY = 0.0016

/**
 * A trackpad pinch arrives as a ctrl-held wheel with much smaller deltas than
 * a mouse wheel, so it needs its own scale to travel the same distance.
 */
const PINCH_SENSITIVITY = 0.01

/** Share of the remaining distance the camera covers each frame. */
const ZOOM_EASING = 0.22

/** Below this the camera is close enough and the animation stops. */
const ZOOM_SETTLE_EPSILON = 0.0005

export interface WheelLike {
  deltaY: number
  deltaMode?: number
  ctrlKey?: boolean
}

export function clampZoom(zoom: number): number {
  return Math.min(CHART_MAX_ZOOM, Math.max(CHART_MIN_ZOOM, zoom))
}

export function normalizedWheelDelta(event: WheelLike): number {
  const mode = event.deltaMode ?? 0
  const scale = mode === 1 ? WHEEL_LINE_HEIGHT : mode === 2 ? WHEEL_PAGE_HEIGHT : 1
  return event.deltaY * scale
}

/**
 * The zoom a wheel notch asks for. It is exponential rather than additive so a
 * notch changes the view by the same proportion however far in you already are.
 */
export function zoomAfterWheel(current: number, event: WheelLike): number {
  const sensitivity = event.ctrlKey ? PINCH_SENSITIVITY : WHEEL_SENSITIVITY
  return clampZoom(current * Math.exp(-normalizedWheelDelta(event) * sensitivity))
}

/** One frame of travel from where the camera is towards where it is headed. */
export function easeZoom(current: number, target: number): number {
  const next = current + (target - current) * ZOOM_EASING
  return Math.abs(target - next) <= ZOOM_SETTLE_EPSILON ? target : next
}

export function zoomPercentLabel(zoom: number): string {
  return `${Math.round(zoom * 100)}%`
}

/**
 * Wheel and pinch zooming on `paneRef`, eased towards the zoom the gesture
 * asked for and anchored on the point under the pointer, so the card being
 * looked at stays under the cursor.
 *
 * The listener is registered in the capture phase and stops the event there:
 * that is what keeps React Flow's own (stepped) handler from also running, and
 * it is why the canvas is rendered with `zoomOnScroll` off.
 */
export function useSmoothWheelZoom(paneRef: RefObject<HTMLDivElement | null>) {
  const { getViewport, setViewport } = useReactFlow()
  // React Flow's helpers are stable in the library but not in every test
  // double, so the effect reaches them through a ref and never re-registers.
  const cameraRef = useRef({ getViewport, setViewport })
  cameraRef.current = { getViewport, setViewport }

  useEffect(() => {
    const pane = paneRef.current
    if (!pane) return

    let frame = 0
    let target = 0
    // The chart point held still under the pointer, and where that pointer is.
    let anchor = { x: 0, y: 0 }
    let pointer = { x: 0, y: 0 }

    const step = () => {
      frame = 0
      const { zoom } = cameraRef.current.getViewport()
      const next = easeZoom(zoom, target)
      cameraRef.current.setViewport(
        { x: pointer.x - anchor.x * next, y: pointer.y - anchor.y * next, zoom: next },
        { duration: 0 },
      )
      if (next !== target) frame = requestAnimationFrame(step)
    }

    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      event.stopPropagation()

      const view = cameraRef.current.getViewport()
      // A second notch while the camera is still moving builds on where it is
      // headed, not on where it happens to be this frame.
      const from = frame ? target : view.zoom
      const next = zoomAfterWheel(from, event)
      if (next === from) return

      const rect = pane.getBoundingClientRect()
      pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top }
      anchor = { x: (pointer.x - view.x) / view.zoom, y: (pointer.y - view.y) / view.zoom }
      target = next
      if (!frame) frame = requestAnimationFrame(step)
    }

    pane.addEventListener('wheel', onWheel, { passive: false, capture: true })
    return () => {
      pane.removeEventListener('wheel', onWheel, { capture: true })
      if (frame) cancelAnimationFrame(frame)
    }
  }, [paneRef])
}
